import React from 'react';
import { QuestionDefinition } from '../../../types/survey.types';
import TextInputRenderer from './TextInputRenderer';
import NumberInputRenderer from './NumberInputRenderer';
import DatePickerRenderer from './DatePickerRenderer';
import RadioGroupRenderer from './RadioGroupRenderer';
import CheckboxGroupRenderer from './CheckboxGroupRenderer';
import DropdownRenderer from './DropdownRenderer';
import RatingRenderer from './RatingRenderer';
import ScaleRenderer from './ScaleRenderer';
import YesNoRenderer from './YesNoRenderer';
import DosingScheduleRenderer from './DosingScheduleRenderer';
import MeasurementRenderer from './MeasurementRenderer';

export interface QuestionRendererProps {
  question: QuestionDefinition;
  value: any;
  onChange: (value: any) => void;
  error?: string;
  questionNumber?: number;
}

const QuestionRenderer: React.FC<QuestionRendererProps> = ({
  question,
  value,
  onChange,
  error,
  questionNumber,
}) => {
  // Render question label
  const renderLabel = () => (
    <div className="mb-3">
      <label className="block text-lg font-semibold text-gray-900">
        {questionNumber && <span className="text-gray-500 mr-2">Q{questionNumber}.</span>}
        {question.title}
        {question.required && <span className="text-red-500 ml-1">*</span>}
      </label>
      {question.description && (
        <p className="text-sm text-gray-600 mt-1">{question.description}</p>
      )}
    </div>
  );

  // Render error message
  const renderError = () => {
    if (!error) return null;

    return (
      <p className="error-message text-sm text-red-600 mt-2 flex items-center">
        <span className="mr-1">⚠️</span>
        {error}
      </p>
    );
  };

  // Route to appropriate renderer based on question type
  const renderInput = () => {
    const commonProps = {
      question,
      value,
      onChange,
      error,
    };

        switch (question.type) {
      case 'text':
      case 'email':
      case 'textarea':
        return <TextInputRenderer {...commonProps} />;

      case 'number':
        return <NumberInputRenderer {...commonProps} />;

      case 'date':
        return <DatePickerRenderer {...commonProps} />;

      case 'radio':
        return <RadioGroupRenderer {...commonProps} />;

      case 'checkbox':
        return <CheckboxGroupRenderer {...commonProps} />;

      case 'dropdown':
        return <DropdownRenderer {...commonProps} />;

      case 'rating':
        return <RatingRenderer {...commonProps} />;

      case 'scale':
        return <ScaleRenderer {...commonProps} />;

      case 'yesno':
        return <YesNoRenderer {...commonProps} />;

      case 'dosing_schedule':
        return <DosingScheduleRenderer {...commonProps} />;

      case 'measurement':
        return <MeasurementRenderer {...commonProps} />;

      default:
        console.warn(`Unknown question type: ${question.type}`);
        return (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-yellow-800">
            <p className="font-medium">Unsupported Question Type</p>
            <p className="text-sm mt-1">
              Question type "{question.type}" is not yet implemented.
            </p>
          </div>
        );
    }
  };

  return (
    <div className="question-renderer">
      {renderLabel()}
      {renderInput()}
      {renderError()}
    </div>
  );
};

export default QuestionRenderer;
